import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FadeIn from '../components/FadeIn';
import WeddingFlow from '../components/WeddingFlow';
import TrustSignals from '../components/TrustSignals';
import WhyTour from '../components/WhyTour';
import AiSnapshot from '../components/AiSnapshot';

export default function WeddingsHub() {
  return (
    <main className="bg-brand-vintage pb-0">
      <SeoMeta 
        title="Weddings at Padua | A Spanish Revival Venue in Claremont, CA" 
        description="Experience a cinematic Spanish Revival wedding venue in Claremont, CA. Padua offers breathtaking spaces, elevated food, and seamless guest flow." 
      />
      
      <PageHero 
        title="A Cinematic Spanish Revival Venue" 
        subtitle="Nestled in the scenic foothills, Padua offers a romantic, architectural canvas for an unforgettable celebration."
        tag="Claremont, California"
      />

      {/* Intro */}
      <section className="py-24 md:py-32 px-6 max-w-4xl mx-auto text-center">
        <FadeIn>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal mb-8 font-light">
            An Elevated Foundation for Your Day
          </h2>
          <div className="w-16 h-[1px] bg-brand-stone mx-auto mb-10" />
          <p className="font-sans text-brand-charcoal/80 text-lg leading-relaxed font-light mb-8">
            At Padua, your wedding flows seamlessly from one breathtaking space to the next. From a sunlit ceremony in the olive grove to an evening of elevated dining and dancing in our historic ballroom, we provide a sophisticated, private estate setting that feels like a destination wedding right in Southern California.
          </p>
        </FadeIn>
      </section>

      <TrustSignals />
      <WhyTour />

      {/* Feature Grid */}
      <section className="py-24 bg-brand-aged px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16">
          
          <FadeIn>
            <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl mb-8">
              <img src="https://ik.imagekit.io/aeu02xvjgn/09-padua-hills-claremont-wedding-photography.jpg" alt="Ceremony" className="w-full h-full object-cover" />
            </div>
            <h3 className="font-serif text-3xl text-brand-charcoal mb-4 font-light">The Ceremony</h3>
            <p className="font-sans text-brand-charcoal/80 leading-relaxed font-light mb-6">
              Exchange vows in our Plaza Courtyard under century-old olive trees. A stunning, open-air sanctuary that feels grand yet intimate.
            </p>
            <Link to="/weddings/ceremony" className="text-[10px] uppercase tracking-[0.25em] text-brand-olive border-b border-brand-olive pb-1 font-bold">Explore the Ceremony</Link>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl mb-8">
              <img src="https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Wedding-Photos-Madison-and-Daniel-31.jpg" alt="Reception" className="w-full h-full object-cover" />
            </div>
            <h3 className="font-serif text-3xl text-brand-charcoal mb-4 font-light">The Reception</h3>
            <p className="font-sans text-brand-charcoal/80 leading-relaxed font-light mb-6">
              Step into the Historic Ballroom for an evening of elegance. Vaulted ceilings and original hardwood floors set the stage for dinner and dancing.
            </p>
            <Link to="/weddings/reception" className="text-[10px] uppercase tracking-[0.25em] text-brand-olive border-b border-brand-olive pb-1 font-bold">Explore the Reception</Link>
          </FadeIn>

          <FadeIn>
            <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl mb-8">
              <img src="https://ik.imagekit.io/aeu02xvjgn/2020-06-10-Padua-Menu-0169.jpg" alt="Food & Catering" className="w-full h-full object-cover" />
            </div>
            <h3 className="font-serif text-3xl text-brand-charcoal mb-4 font-light">Food & Hospitality</h3>
            <p className="font-sans text-brand-charcoal/80 leading-relaxed font-light mb-6">
              Your wedding meal should rival your favorite restaurant. We offer chef-driven menus, premium bar experiences, and impeccable service.
            </p>
            <Link to="/weddings/food-and-catering" className="text-[10px] uppercase tracking-[0.25em] text-brand-olive border-b border-brand-olive pb-1 font-bold">Explore Catering</Link>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl mb-8">
              <img src="https://ik.imagekit.io/aeu02xvjgn/34-padua-hills-theatre-claremont-persian-wedding-photography.jpg" alt="Spanish Revival Venue" className="w-full h-full object-cover" />
            </div>
            <h3 className="font-serif text-3xl text-brand-charcoal mb-4 font-light">The Estate Atmosphere</h3>
            <p className="font-sans text-brand-charcoal/80 leading-relaxed font-light mb-6">
              Escape the standard hotel ballroom. Discover the authenticity, architectural beauty, and photo moments of our 1930s Spanish Revival estate.
            </p>
            <Link to="/weddings/spanish-revival-venue" className="text-[10px] uppercase tracking-[0.25em] text-brand-olive border-b border-brand-olive pb-1 font-bold">Explore the Estate</Link>
          </FadeIn>

        </div>
      </section>

      <WeddingFlow />
      <AiSnapshot data={{ venueType: "Historic Southern California Wedding Venue" }} />
      <CTASection />
    </main>
  );
}